import React, { useEffect, useState } from 'react';
import { BlocManagementAdmins, AdminViewState } from '../../../bloc/BlocManagementAdmins';
import { AdminWithRoles, CreateAdminRequest, UpdateAdminRequest } from '../../../../service/supabase/AdminManagementService';
import StreamBuilder from '../../../components/common/StreamBuilder';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './AdminsManagementPage.css';

import Button from '../../../components/common/Button';
import Input from '../../../components/common/Input';
import Card from '../../../components/common/Card';
import Loading from '../../../components/common/Loading';
import AdminProtectedRoute from '../../../components/admin/AdminProtectedRoute';
import AdminLayout from '../../../components/admin/AdminLayout';
import CpnAdminForm from './components/CpnAdminForm';
import { useAdmin } from '../../../../base/AdminContext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { confirmDialog } from 'primereact/confirmdialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './AdminsManagementPage.css';

const AdminsManagementPage: React.FC = () => {
    const { admin, checkPermission } = useAdmin();
    const [bloc] = useState(() => new BlocManagementAdmins());
    const [searchTerm, setSearchTerm] = useState('');

    // Check permissions for admin management
    const canAddAdmin = checkPermission('admin_management', 'admin_list', 'add');
    const canEditAdmin = checkPermission('admin_management', 'admin_list', 'edit');
    const canDeleteAdmin = checkPermission('admin_management', 'admin_list', 'delete');

    useEffect(() => {
        bloc.initialize();
        return () => bloc.dispose();
    }, [bloc]);

    // Search handler with debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            bloc.setFilters({ search: searchTerm });
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, bloc]);

    const handleCreateAdmin = (data: CreateAdminRequest) => {
        if (!canAddAdmin) {
            console.warn('Không có quyền tạo admin');
            return;
        }
        const adminData = {
            ...data,
            created_by: admin?.full_name || 'Unknown'
        };
        bloc.createAdmin(adminData);
    };

    const handleUpdateAdmin = (data: UpdateAdminRequest) => {
        if (!canEditAdmin) {
            console.warn('Không có quyền sửa admin');
            return;
        }
        const adminData = {
            ...data,
            created_by: admin?.full_name || 'Unknown'
        };
        bloc.updateAdmin(adminData);
    };

    const handleSubmitForm = (data: CreateAdminRequest | UpdateAdminRequest) => {
        if ('id' in data) {
            handleUpdateAdmin(data as UpdateAdminRequest);
        } else {
            handleCreateAdmin(data as CreateAdminRequest);
        }
    };

    const handleDeleteAdmin = (admin: AdminWithRoles) => {
        if (!canDeleteAdmin) {
            console.warn('Không có quyền xóa admin');
            return;
        }

        confirmDialog({
            message: `Bạn có chắc chắn muốn xóa quản trị viên ${admin.full_name}? Hành động này không thể hoàn tác.`,
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Xóa',
            rejectLabel: 'Hủy',
            acceptClassName: 'p-button-danger',
            accept: () => {
                bloc.info.adminToDelete = admin;
                bloc.deleteAdmin();
            }
        });
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('vi-VN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Column templates for DataTable
    const nameBodyTemplate = (rowData: AdminWithRoles) => {
        return (
            <div className="admin-info">
                <span className="admin-name">{rowData.full_name}</span>
            </div>
        );
    };

    const usernameBodyTemplate = (rowData: AdminWithRoles) => {
        return <span className="admin-username">{rowData.user_name}</span>;
    };

    const rolesBodyTemplate = (rowData: AdminWithRoles) => {
        return (
            <div className="roles-display">
                {rowData.roles && rowData.roles.length > 0 ? (
                    rowData.roles.map(role => (
                        <Tag key={role.id} value={role.name} className="mr-1" />
                    ))
                ) : (
                    <span className="no-roles">Không có vai trò</span>
                )}
            </div>
        );
    };

    const dateBodyTemplate = (rowData: AdminWithRoles) => {
        return (
            <span className="date-created">
                {rowData.created_at ? formatDate(rowData.created_at) : '-'}
            </span>
        );
    };

    const actionBodyTemplate = (rowData: AdminWithRoles) => {
        return (
            <div className="action-buttons">
                {canEditAdmin && (
                    <Button
                        variant="secondary"
                        size="small"
                        onClick={() => bloc.openEditForm(rowData)}
                    >
                        Sửa
                    </Button>
                )}
                {canDeleteAdmin && (
                    <Button
                        variant="danger"
                        size="small"
                        onClick={() => handleDeleteAdmin(rowData)}
                    >
                        Xóa
                    </Button>
                )}
            </div>
        );
    };

    // DataTable header template
    const tableHeader = () => {
        return (
            <div className="table-header">
                <div className="table-header-left">
                    <span>Danh sách quản trị viên</span>
                </div>
                <div className="table-header-right">
                    <Button
                        variant="secondary"
                        size="small"
                        onClick={() => bloc.refreshAdmins()}
                        className="mr-2"
                    >
                        <i className="pi pi-refresh" style={{ marginRight: '4px' }}></i>
                        Làm mới
                    </Button>
                    {canAddAdmin && (
                        <Button
                            variant="primary"
                            size="small"
                            onClick={() => bloc.openCreateForm()}
                        >
                            <i className="pi pi-plus" style={{ marginRight: '4px' }}></i>
                            Thêm quản trị viên
                        </Button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <AdminProtectedRoute
            requiredModule="admin_management"
            requiredSubModule="admin_list"
            requiredPermission="view"
        >
            <AdminLayout>
                <div className="admins-management-page">

                    <StreamBuilder
                        stream={bloc.getStream('adminViewState')}
                        initialData={bloc.info}
                        builder={(snapshot) => {
                            const state: AdminViewState = snapshot.data || bloc.info;
                            return (
                                <>


                                    {/* Content */}
                                    <Card className="">
                                        {state.loading && !state.hasData ? (
                                            <div className="loading-container">
                                                <Loading />
                                                <p>Đang tải dữ liệu...</p>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Data Table */}
                                                <div className="table-container">
                                                    <DataTable
                                                        scrollHeight='calc(100vh - 200px)'
                                                        value={state.admins}
                                                        tableStyle={{ minWidth: '50rem' }}
                                                        emptyMessage={searchTerm ?
                                                            'Không tìm thấy quản trị viên nào phù hợp' :
                                                            'Chưa có quản trị viên nào'
                                                        }
                                                        loading={state.loading}
                                                        stripedRows
                                                        showGridlines
                                                        header={tableHeader}
                                                    >
                                                        <Column
                                                            field="full_name"
                                                            header="Họ và tên"
                                                            body={nameBodyTemplate}
                                                            sortable
                                                        />
                                                        <Column
                                                            field="user_name"
                                                            header="Tên đăng nhập"
                                                            body={usernameBodyTemplate}
                                                            sortable
                                                        />
                                                        <Column
                                                            field="roles"
                                                            header="Vai trò"
                                                            body={rolesBodyTemplate}
                                                        />
                                                        <Column
                                                            field="created_at"
                                                            header="Ngày tạo"
                                                            body={dateBodyTemplate}
                                                            sortable
                                                        />
                                                        {(canEditAdmin || canDeleteAdmin) && (
                                                            <Column
                                                                header="Thao tác"
                                                                body={actionBodyTemplate}
                                                                style={{ width: '150px' }}
                                                            />
                                                        )}
                                                    </DataTable>
                                                </div>

                                                {/* Pagination */}
                                                {state.pagination.totalPages > 1 && (
                                                    <div className="pagination">
                                                        <div className="pagination-info">
                                                            <span>
                                                                Hiển thị {state.admins.length} / {state.pagination.total} quản trị viên
                                                            </span>
                                                        </div>
                                                        <div className="pagination-controls">
                                                            <Button
                                                                variant="secondary"
                                                                size="small"
                                                                disabled={state.pagination.page <= 1}
                                                                onClick={() => bloc.goToPage(state.pagination.page - 1)}
                                                            >
                                                                Trước
                                                            </Button>
                                                            <span className="page-info">
                                                                Trang {state.pagination.page} / {state.pagination.totalPages}
                                                            </span>
                                                            <Button
                                                                variant="secondary"
                                                                size="small"
                                                                disabled={state.pagination.page >= state.pagination.totalPages}
                                                                onClick={() => bloc.goToPage(state.pagination.page + 1)}
                                                            >
                                                                Sau
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Error Display */}
                                        {state.error && (
                                            <div className="error-banner">
                                                <span>{state.error}</span>
                                                <Button
                                                    variant="secondary"
                                                    size="small"
                                                    onClick={() => bloc.clearError()}
                                                >
                                                    Đóng
                                                </Button>
                                            </div>
                                        )}
                                    </Card>

                                    {/* Admin Form */}
                                    {((state.formType === 'create' && canAddAdmin) || (state.formType === 'edit' && canEditAdmin)) && (
                                        <CpnAdminForm
                                            isOpen={state.isFormOpen}
                                            onClose={() => bloc.closeForm()}
                                            onSubmit={handleSubmitForm}
                                            admin={state.selectedAdmin}
                                            availableRoles={state.availableRoles}
                                            formType={state.formType}
                                            loading={state.loading}
                                            error={state.error}
                                        />
                                    )}

                                    {/* Confirm Dialog */}
                                    <ConfirmDialog />
                                </>
                            );
                        }}
                    />
                </div>
            </AdminLayout>
        </AdminProtectedRoute>
    );
};

export default AdminsManagementPage;
